import { cva, type VariantProps } from 'class-variance-authority';

import { AudioLines } from '../../icons/lucide/AudioLines';
import { NotepadText } from '../../icons/lucide/NotepadText';
import { SquareCheckBig } from '../../icons/lucide/SquareCheckBig';
import { Timer } from '../../icons/lucide/Timer';
import { DARK_LAYER1 } from '../../styles/elevation';
import type { BadgeProps } from '../Badge';
import type { IconComponent } from '../Button';
import type { PractiseCardType } from './PractiseCard.types';

/**
 * Container geometry per view — mirrors the Figma "Quiz cards updated" frame
 * (`YZK9r3wDhvw1pY60KlVEIj`, node `3153:44675`). Both views are a FLAT card
 * (no scooped body panel like DeckCard): soft `secondary-50/80` surface,
 * `rounded-[24px]`, and the flat `0 2 8 rgba(0,0,0,.06)` "Shadow-Secondary"
 * (web token `card-row`; native gets `CARD_NATIVE_SHADOW.list`).
 *
 *   - grid — fixed 181-wide tile, `gap-1.5` / `p-3` (Figma 6 / 12), lighter
 *     `secondary-100` border.
 *   - list — full-width row (capped at the design's 360), `gap-2` / `p-4`
 *     (Figma 8 / 16), `secondary-200` border.
 *
 * The border sits on the container itself (unlike the folder/deck cards, whose
 * body panel forces a border overlay) — this card has no full-bleed inner
 * surface to align to.
 */
export const practiseCardVariants = cva(
  // Dark: a list row is a content SURFACE, not the -50 chrome (elevation.ts).
  `relative flex flex-col rounded-[24px] bg-secondary-50/80 ${DARK_LAYER1} web:shadow-card-row`,
  {
    variants: {
      view: {
        grid: 'h-[120px] w-[181px] justify-between gap-1.5 border border-secondary-100 p-3',
        list: 'w-full max-w-[560px] gap-2 border border-secondary-200 p-4',
      },
    },
    defaultVariants: { view: 'grid' },
  },
);

/**
 * Per-type leading icon + Badge colour (Figma's "Type" property). The colour
 * axis maps 1:1 onto the shared Badge's semantic scale — `success` → green,
 * `warning` → orange, `info` → tertiary (blue), `primary` → violet — so the
 * icon chip AND (for completed cards) the status pill reuse one token source.
 * Icon glyphs are the Figma `data-name`s: square-check-big / audio-lines /
 * timer / notepad-text.
 */
export const PRACTISE_TYPE_META: Record<
  PractiseCardType,
  { colour: NonNullable<BadgeProps['colour']>; icon: IconComponent }
> = {
  quiz: { colour: 'success', icon: SquareCheckBig },
  audio: { colour: 'warning', icon: AudioLines },
  exam: { colour: 'info', icon: Timer },
  caseStudy: { colour: 'primary', icon: NotepadText },
};

export type PractiseCardVariantProps = VariantProps<typeof practiseCardVariants>;
